/**
 * Shared capital plans — named cash-planning overlays the CEO / COO / CFO
 * can save, comment on, and decide. Pure helpers (no Firebase).
 */

import type { CRMUser, EmployeeDesignation, ExecutiveBriefingProfile } from '../types';
import {
  mergeStackedScenarioLines,
  scenarioHasEffect,
  type ScenarioCustomSlice,
  type ScenarioLine,
  type ScenarioOutflow,
  type ScenarioOutflowAttachment,
  type ScenarioProject,
  type ScenarioTimingShift,
} from './cashFlowScenario';

export type CapitalPlanStatus =
  | 'draft'
  | 'shared'
  | 'needs_info'
  | 'approved'
  | 'completed'
  | 'deferred'
  | 'declined';

export const CAPITAL_PLAN_STATUSES: readonly CapitalPlanStatus[] = [
  'draft',
  'shared',
  'needs_info',
  'approved',
  'completed',
  'deferred',
  'declined',
];

export const CAPITAL_PLAN_STATUS_LABELS: Record<CapitalPlanStatus, string> = {
  draft: 'Draft',
  shared: 'Shared',
  needs_info: 'Needs info',
  approved: 'Approved',
  completed: 'Completed',
  deferred: 'Deferred',
  declined: 'Declined',
};

/** Approved plans stay on the forecast; completed ones are archived off it. */
export function isCapitalPlanOnForecast(status: CapitalPlanStatus): boolean {
  return status === 'approved';
}

export interface CapitalPlan {
  id: string;
  title: string;
  why: string;
  status: CapitalPlanStatus;
  lines: ScenarioLine[];
  createdByUserId: string;
  createdByName: string;
  updatedByUserId: string;
  updatedByName: string;
  createdAt: Date;
  updatedAt: Date;
  decidedByUserId?: string;
  decidedByName?: string;
  decidedAt?: Date;
  decisionNote?: string;
}

export interface CapitalPlanComment {
  id: string;
  body: string;
  bodyHtml?: string;
  authorUserId: string;
  authorName: string;
  createdAt: Date;
  mentionUserIds: string[];
}

/** TipTap HTML if the stored why already is markup; otherwise wrap plain text. */
export function capitalPlanWhyToHtml(why: string): string {
  const t = (why || '').trim();
  if (!t) return '';
  if (t.startsWith('<')) return t;
  const escaped = t
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return `<p>${escaped.replace(/\n/g, '<br>')}</p>`;
}

export function stripCapitalPlanHtml(value: string): string {
  return (value || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

export function isCapitalPlanStatus(value: unknown): value is CapitalPlanStatus {
  return typeof value === 'string' && (CAPITAL_PLAN_STATUSES as readonly string[]).includes(value);
}

/** CEO (admin) or COO may approve / defer / decline. */
export function canDecideCapitalPlan(args: {
  authRole: string | undefined;
  designations: EmployeeDesignation[] | string[] | undefined;
  briefingProfile?: ExecutiveBriefingProfile | null;
}): boolean {
  if (args.authRole === 'admin') return true;
  return !!args.designations?.includes('coo') || args.briefingProfile === 'coo';
}

export function canTransitionCapitalPlanStatus(
  from: CapitalPlanStatus,
  to: CapitalPlanStatus,
  args: { canDecide: boolean },
): boolean {
  if (from === to) return true;
  if (to === 'draft') return from === 'draft';
  if (to === 'shared') return from === 'draft' || from === 'needs_info' || from === 'shared';
  if (to === 'needs_info') {
    return from === 'shared' || from === 'draft' || from === 'needs_info';
  }
  if (to === 'completed') {
    return from === 'approved' || from === 'completed';
  }
  if (to === 'approved' || to === 'deferred' || to === 'declined') {
    if (!args.canDecide) return false;
    if (to === 'approved' && from === 'completed') return true;
    return from === 'shared' || from === 'needs_info' || from === 'draft';
  }
  return false;
}

function toIsoDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function fromIsoDate(s: string): Date | null {
  const [y, m, d] = String(s || '').split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function parseDate(raw: unknown): Date | null {
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw;
  if (typeof raw === 'string') {
    const iso = fromIsoDate(raw);
    if (iso) return iso;
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (raw && typeof raw === 'object' && 'toDate' in raw && typeof (raw as { toDate: () => Date }).toDate === 'function') {
    const d = (raw as { toDate: () => Date }).toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export const CAPEX_LINE_ATTACHMENT_MAX_BYTES = 15 * 1024 * 1024;
export const CAPEX_VIEWER_LEAD_ID = '__capex__';
export const CAPEX_LINE_ATTACHMENT_ACCEPT = 'application/pdf,image/*,.pdf,.png,.jpg,.jpeg,.gif,.webp,.heic,.heif';

export type CapexLineAttachmentKind = 'pdf' | 'image';

export function capexLineAttachmentPreviewKind(file: {
  name?: string;
  contentType?: string;
  storagePath?: string;
}): CapexLineAttachmentKind | null {
  const type = (file.contentType || '').toLowerCase();
  const name = `${file.name || ''} ${file.storagePath || ''}`.toLowerCase();
  if (type.includes('pdf') || name.includes('.pdf')) return 'pdf';
  if (type.startsWith('image/') || /\.(png|jpe?g|gif|webp|heic|heif|bmp|svg)(\b|$)/i.test(name)) return 'image';
  return null;
}

export function capexLineAttachmentContentType(file: { name?: string; type?: string }): string {
  const type = (file.type || '').toLowerCase();
  if (type === 'application/pdf' || type.startsWith('image/')) return file.type || type;
  const name = (file.name || '').toLowerCase();
  if (name.endsWith('.pdf')) return 'application/pdf';
  if (name.endsWith('.png')) return 'image/png';
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
  if (name.endsWith('.webp')) return 'image/webp';
  if (name.endsWith('.gif')) return 'image/gif';
  if (name.endsWith('.heic') || name.endsWith('.heif')) return 'image/heic';
  return 'application/octet-stream';
}

export function assertCapexLineAttachmentFile(file: Pick<File, 'name' | 'type' | 'size'>): void {
  if (file.size > CAPEX_LINE_ATTACHMENT_MAX_BYTES) {
    throw new Error(`${file.name} is larger than 15 MB.`);
  }
  if (!capexLineAttachmentPreviewKind({ name: file.name, contentType: file.type })) {
    throw new Error(`${file.name} must be a PDF or image.`);
  }
}

export function parseCapexLineAttachments(raw: unknown): ScenarioOutflowAttachment[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item): ScenarioOutflowAttachment | null => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Record<string, unknown>;
      if (typeof row.name !== 'string' || typeof row.storagePath !== 'string') return null;
      if (!row.name.trim() || !row.storagePath.trim()) return null;
      return {
        id: typeof row.id === 'string' && row.id.trim() ? row.id.trim() : row.storagePath,
        name: row.name,
        storagePath: row.storagePath,
        contentType: typeof row.contentType === 'string' ? row.contentType : undefined,
        size: typeof row.size === 'number' ? row.size : undefined,
        url: typeof row.url === 'string' ? row.url : undefined,
      };
    })
    .filter((row): row is ScenarioOutflowAttachment => !!row);
}

function serializeOutflow(line: ScenarioOutflow): Record<string, unknown> {
  const attachments = (line.attachments || []).map((file) => ({
    id: file.id,
    name: file.name,
    storagePath: file.storagePath,
    contentType: file.contentType ?? null,
    size: file.size ?? null,
    url: file.url ?? null,
  }));
  return {
    id: line.id,
    type: line.type,
    enabled: line.enabled,
    label: line.label,
    vendorName: line.vendorName,
    amount: line.amount,
    date: toIsoDate(line.date),
    split: line.split,
    netDays: line.netDays,
    depositPercent: line.depositPercent,
    againstItemKey: line.againstItemKey ?? null,
    linkedBillId: line.linkedBillId?.trim() || null,
    customSlices: line.customSlices.map((s) => ({
      id: s.id,
      amount: s.amount ?? null,
      percent: s.percent ?? null,
      date: toIsoDate(s.date),
      label: s.label ?? null,
    })),
    ...(attachments.length ? { attachments } : {}),
  };
}

function parseOutflow(rec: Record<string, unknown>): ScenarioOutflow | null {
  if (rec.type !== 'purchase' && rec.type !== 'payment') return null;
  const date = parseDate(rec.date) ?? new Date();
  const customSlices: ScenarioCustomSlice[] = [];
  if (Array.isArray(rec.customSlices)) {
    for (const s of rec.customSlices) {
      if (!s || typeof s !== 'object') continue;
      const slice = s as Record<string, unknown>;
      const sliceDate = parseDate(slice.date);
      if (!sliceDate) continue;
      customSlices.push({
        id: String(slice.id || ''),
        amount: typeof slice.amount === 'number' ? slice.amount : undefined,
        percent: typeof slice.percent === 'number' ? slice.percent : undefined,
        date: sliceDate,
        label: typeof slice.label === 'string' && slice.label ? slice.label : undefined,
      });
    }
  }
  const outflow: ScenarioOutflow = {
    id: String(rec.id || ''),
    type: rec.type,
    enabled: rec.enabled !== false,
    label: String(rec.label || ''),
    vendorName: String(rec.vendorName || ''),
    amount: Number(rec.amount) || 0,
    date,
    split: rec.split === 'fifty_fifty' || rec.split === 'deposit_net' || rec.split === 'custom'
      ? rec.split
      : 'lump',
    netDays: Number(rec.netDays) || 30,
    depositPercent: Number(rec.depositPercent) || 50,
    customSlices,
    againstItemKey: typeof rec.againstItemKey === 'string' && rec.againstItemKey
      ? rec.againstItemKey
      : undefined,
    linkedBillId: typeof rec.linkedBillId === 'string' && rec.linkedBillId.trim()
      ? rec.linkedBillId.trim()
      : undefined,
  };
  const attachments = parseCapexLineAttachments(rec.attachments);
  if (attachments.length) outflow.attachments = attachments;
  return outflow.id ? outflow : null;
}

/** JSON-safe line payload for Firestore (dates as YYYY-MM-DD). */
export function serializeScenarioLines(lines: ScenarioLine[]): unknown[] {
  return lines.map((line) => {
    if (line.type === 'shift') {
      return {
        id: line.id,
        type: 'shift',
        enabled: line.enabled,
        itemKey: line.itemKey,
        shiftDays: line.shiftDays,
      };
    }
    if (line.type === 'project') {
      return {
        id: line.id,
        type: 'project',
        enabled: line.enabled,
        label: line.label,
        items: line.items.map(serializeOutflow),
      };
    }
    return serializeOutflow(line);
  });
}

export function parseScenarioLines(raw: unknown): ScenarioLine[] {
  if (!Array.isArray(raw)) return [];
  const out: ScenarioLine[] = [];
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue;
    const rec = row as Record<string, unknown>;
    if (rec.type === 'shift') {
      const shift: ScenarioTimingShift = {
        id: String(rec.id || ''),
        type: 'shift',
        enabled: rec.enabled !== false,
        itemKey: String(rec.itemKey || ''),
        shiftDays: Number(rec.shiftDays) || 0,
      };
      if (shift.id) out.push(shift);
      continue;
    }
    if (rec.type === 'project') {
      const items = Array.isArray(rec.items)
        ? rec.items
          .map((item) => (item && typeof item === 'object' ? parseOutflow(item as Record<string, unknown>) : null))
          .filter((item): item is ScenarioOutflow => !!item)
        : [];
      const project: ScenarioProject = {
        id: String(rec.id || ''),
        type: 'project',
        enabled: rec.enabled !== false,
        label: String(rec.label || 'Untitled project'),
        items,
      };
      if (project.id) out.push(project);
      continue;
    }
    const outflow = parseOutflow(rec);
    if (outflow) out.push(outflow);
  }
  return out;
}

export function extractMentionUserIds(
  body: string,
  candidates: Array<{ uid: string; displayName: string }>,
): string[] {
  const text = body.toLowerCase();
  const ids: string[] = [];
  for (const c of candidates) {
    const name = c.displayName.trim();
    if (!name) continue;
    const first = name.split(/\s+/)[0];
    const needles = [name, first].filter(Boolean).map((n) => n.toLowerCase());
    if (needles.some((n) => text.includes(`@${n}`))) ids.push(c.uid);
  }
  return Array.from(new Set(ids));
}

export function capitalPlanPath(planId: string): string {
  return `/finances?plan=${encodeURIComponent(planId)}`;
}

/** True when a draft is real user content and the open plan has finished hydrating. */
export function shouldAutosaveCapitalPlan(args: {
  activePlanId?: string | null;
  hydratedPlanId?: string | null;
  /** False while React still holds the previous plan's title/why/lines. */
  draftReady?: boolean;
  title: string;
  why: string;
  lines: unknown[];
}): boolean {
  if (args.draftReady === false) return false;
  if (args.activePlanId && args.hydratedPlanId !== args.activePlanId) return false;
  const whyText = String(args.why || '').replace(/<[^>]+>/g, '').trim();
  return args.title.trim() !== '' || whyText !== '' || args.lines.length > 0;
}

/** Enabled cash-outs from approved plans (timing-shifts stay sandbox-only). */
function withPlanName(lines: ScenarioLine[], title: string): ScenarioLine[] {
  const name = String(title || '').trim();
  if (!name) return lines;
  return lines.map((line) => {
    if (line.type === 'purchase' || line.type === 'payment') {
      return line.vendorName.trim() ? line : { ...line, vendorName: name };
    }
    if (line.type === 'project' && !line.label.trim()) {
      return { ...line, label: name };
    }
    return line;
  });
}

export function bakedApprovedCapexLines(
  plans: Array<Pick<CapitalPlan, 'id' | 'status' | 'title' | 'lines'>>,
  active?: {
    id: string | null | undefined;
    status?: CapitalPlanStatus;
    lines: ScenarioLine[];
    /** When false/omitted, always use the saved plan lines (do not let an empty editor wipe bake-in). */
    useLocalLines?: boolean;
  },
): ScenarioLine[] {
  return plans.flatMap((plan) => {
    if (!isCapitalPlanOnForecast(plan.status)) return [];
    const lines = active?.id === plan.id && active.useLocalLines === true
      ? active.lines
      : plan.lines;
    return withPlanName(lines.filter((line) => line.type !== 'shift'), plan.title);
  });
}

/**
 * Clicking a plan replaces the primary overlay. Compare pins stay;
 * the previous selection is dropped unless it was already pinned.
 */
export function selectCapitalPlanOverlay(args: {
  selectedId: string | null;
  previousSelectedId?: string | null;
  previousStackedIds: string[];
}): string[] {
  const pins = args.previousStackedIds.filter(
    (id) => id !== args.previousSelectedId && id !== args.selectedId,
  );
  if (!args.selectedId) return pins;
  return [args.selectedId, ...pins];
}

/** Pin/unpin a draft for comparison. The selected plan cannot be unpinned. */
export function toggleCapitalPlanComparePin(args: {
  planId: string;
  selectedId: string | null;
  stackedIds: string[];
}): string[] {
  if (args.planId === args.selectedId) {
    return args.stackedIds.includes(args.planId)
      ? args.stackedIds
      : [args.planId, ...args.stackedIds];
  }
  if (args.stackedIds.includes(args.planId)) {
    return args.stackedIds.filter((id) => id !== args.planId);
  }
  return [...args.stackedIds, args.planId];
}

/** Date-first copy for when cash goes negative. Never includes a dollar amount. */
export function formatCapexOutOfCash(args: {
  scenarioFirstRedLabel: string | null;
  liveFirstRedLabel: string | null;
}): { value: string; sub: string; ranOut: boolean } {
  const value = args.scenarioFirstRedLabel ?? 'Stays positive';
  const ranOut = args.scenarioFirstRedLabel != null;
  if (!args.liveFirstRedLabel) {
    return { value, sub: 'Live stays positive', ranOut };
  }
  if (args.liveFirstRedLabel === args.scenarioFirstRedLabel) {
    return { value, sub: `Live also ${args.liveFirstRedLabel}`, ranOut };
  }
  return { value, sub: `Live ${args.liveFirstRedLabel}`, ranOut };
}

/** Draft / shared stack layers — skip approved plans so they are not double-counted. */
export function stackedCapexOverlayLines(args: {
  stackedPlanIds: string[];
  plans: Array<Pick<CapitalPlan, 'id' | 'status' | 'title' | 'lines'>>;
  activePlanId: string | null | undefined;
  activeLines: ScenarioLine[];
}): ScenarioLine[] {
  const layers: Array<{ id: string; lines: ScenarioLine[] }> = [];
  for (const id of args.stackedPlanIds) {
    const plan = args.plans.find((p) => p.id === id);
    if (plan?.status === 'approved' || plan?.status === 'completed') continue;
    if (id === args.activePlanId) {
      layers.push({ id, lines: withPlanName(args.activeLines, plan?.title || '') });
      continue;
    }
    if (plan) layers.push({ id, lines: withPlanName(plan.lines, plan.title) });
  }
  if (!args.activePlanId && scenarioHasEffect(args.activeLines)) {
    layers.push({ id: 'session', lines: args.activeLines });
  }
  return mergeStackedScenarioLines(layers);
}

/** People who can be @mentioned on a capital plan (exec seats + CPA). */
export function isCapitalPlanMentionableUser(u: {
  isActive?: boolean;
  role?: string;
  designations?: EmployeeDesignation[] | string[];
  executiveBriefing?: { enabled?: boolean; profile?: string } | null;
}): boolean {
  if (!u.isActive) return false;
  const designations = u.designations || [];
  const briefing = u.executiveBriefing;
  const isCoo =
    designations.includes('coo')
    || (briefing?.enabled === true && (!briefing.profile || briefing.profile === 'coo'));
  return (
    u.role === 'admin'
    || u.role === 'cpa'
    || designations.includes('executive_assistant')
    || designations.includes('office_manager')
    || designations.includes('cfo')
    || isCoo
  );
}

/** Other exec seats to ping — CEO admins (not COO-briefing), COO, CFO. Skip the actor. */
export function listCapitalPlanNotifyUids(
  crmUsers: CRMUser[],
  actorUid: string,
): string[] {
  const ids = new Set<string>();
  for (const u of crmUsers) {
    if (!u.isActive || u.uid === actorUid) continue;
    const designations = u.designations || [];
    const briefing = u.executiveBriefing;
    const isCoo =
      designations.includes('coo')
      || (briefing?.enabled === true && (!briefing.profile || briefing.profile === 'coo'));
    const isCfo = designations.includes('cfo');
    const isCeoAdmin =
      u.role === 'admin'
      && !(briefing?.enabled === true && briefing.profile === 'coo');
    if (isCeoAdmin || isCoo || isCfo) ids.add(u.uid);
  }
  return Array.from(ids);
}
